import bcrypt from "bcryptjs";
import { prisma } from "../../lib/prisma";
import config from "../../config";
import {
  ICreatePostPayload,
  IPostQuery,
  IUpdatePostPayload,
} from "./post.interface";
import { CommentStatus, PostStatus } from "../../../generated/prisma/enums";
import { skip } from "node:test";
import { PostWhereInput } from "../../../generated/prisma/models";

const createPost = async (payload: ICreatePostPayload, userId: string) => {
  const user = await prisma.user.findUniqueOrThrow({
    where: {
      id: userId,
    },
    include: {
      subscription: true,
    },
  });
  if (payload.isPremium && user.subscription?.status !== "ACTIVE") {
    throw new Error(
      "Your are not premium user. So, you can not create premium post. ",
    );
  }
  const result = await prisma.post.create({
    data: {
      ...payload,
      authorId: userId,
    },
  });

  return result;
};

const getAllPosts = async () => {
  const posts = await prisma.post.findMany({
    include: {
      author: {
        omit: {
          password: true,
        },
      },
      comments: true,
    },
  });
  return posts;
};

// ? Filtering with Exact Match
const getAllPostsWithFilter = async () => {
  const posts = await prisma.post.findMany({
    // ? Filtering without AND condition
    // where: {
    //   title: "My Second Post for USER",
    //   status: PostStatus.PUBLISHED,
    // },

    // ? Filtering with AND condition
    where: {
      AND: [
        {
          title: "My Second Post for USER",
        },
        {
          status: PostStatus.PUBLISHED,
        },
        {
          tags: {
            equals: ["typescript", "prisma", "express"],
          },
        },
      ],
    },
    include: {
      author: {
        omit: {
          password: true,
        },
      },
      comments: true,
    },
  });
  return posts;
};

// ? Search/Filtering with Partial Match OR Operator
const getAllPostsWithSearch = async () => {
  const posts = await prisma.post.findMany({
    where: {
      OR: [
        {
          title: {
            contains: "Second",
            mode: "insensitive",
          },
        },
        {
          status: PostStatus.PUBLISHED,
        },
        {
          tags: {
            equals: ["typescript", "prisma", "express"],
          },
        },
      ],
    },
    include: {
      author: {
        omit: {
          password: true,
        },
      },
      comments: true,
    },
  });
  return posts;
};

// ? Filtering with exact match (AND Operator) and Search with Partial Match (OR Operator)
const getAllPostsWithFilterAndSearch = async () => {
  const posts = await prisma.post.findMany({
    where: {
      AND: [
        // Searching
        {
          OR: [
            {
              title: {
                contains: "ARCHIVED",
                mode: "insensitive",
              },
              content: {
                contains: "Content of the post goes here.",
                mode: "insensitive",
              },
            },
          ],
        },
        // Filtering
        {
          status: PostStatus.ARCHIVED,
        },
        {
          tags: {
            equals: ["typescript", "prisma", "express"],
          },
        },
      ],
    },
    include: {
      author: {
        omit: {
          password: true,
        },
      },
      comments: true,
    },
  });
  return posts;
};

// ? All Post with Pagination
const getAllPostsWithPagination = async () => {
  const posts = await prisma.post.findMany({
    take: 5,
    skip: 5,
    // orderBy: {
    //   createdAt: "desc",
    // },
    include: {
      author: {
        omit: {
          password: true,
        },
      },
      comments: true,
    },
  });
  return posts;
};

// ? All Post with DynamicPagination
const getAllPostsWithDynamicPagination = async (queryParams: IPostQuery) => {
  const limit = queryParams.limit ? parseInt(queryParams.limit) : 5;
  const page = queryParams.page ? parseInt(queryParams.page) : 1;
  const skip = (page - 1) * limit;
  const sortBy = queryParams.sortBy ? queryParams.sortBy : "createdAt";
  const sortOrder = queryParams.sortOrder ? queryParams.sortOrder : "desc";
  const tags = queryParams.tags ? JSON.parse(queryParams.tags as string) : [];
  const tagsArray = Array.isArray(tags) ? tags : [tags];

  console.log(tagsArray);

  const andConditions: PostWhereInput[] = [];
  if (queryParams.searchTerm) {
    andConditions.push({
      OR: [
        {
          title: {
            contains: queryParams.searchTerm,
            mode: "insensitive",
          },
        },
        {
          content: {
            contains: queryParams.searchTerm,
            mode: "insensitive",
          },
        },
      ],
    });
  }
  if (queryParams.status) {
    andConditions.push({ status: queryParams.status });
  }
  if (queryParams.content) {
    andConditions.push({ content: queryParams.content });
  }
  if (queryParams.authorId) {
    andConditions.push({ authorId: queryParams.authorId });
  }
  if (queryParams.isFeatured) {
    andConditions.push({ isFeatured: Boolean(queryParams.isFeatured) });
  }
  if (queryParams.tags) {
    andConditions.push({
      tags: {
        hasSome: tagsArray,
      },
    });
  }

  andConditions.push({
    isPremium: false,
  });

  const posts = await prisma.post.findMany({
    // Options for Dynamic Filtering, Pagination, and Sorting
    where: {
      AND: andConditions,
    },
    take: limit,
    skip: skip,
    orderBy: {
      [sortBy]: sortOrder,
    },
    include: {
      author: {
        omit: {
          password: true,
        },
      },
      comments: true,
    },
  });

  const totalPostCount = await prisma.post.count({
    where: {
      AND: andConditions,
    },
  });
  return {
    data: posts,
    meta: {
      page: page,
      limit: limit,
      total: totalPostCount,
      totalPages: Math.ceil(totalPostCount / limit),
    },
  };
};

const getPostById = async (postId: string) => {
  const transactionResult = await prisma.$transaction(async (tx) => {
    await tx.post.update({
      where: {
        id: postId,
      },
      data: {
        views: {
          increment: 1,
        },
      },
    });

    // throw new Error("Fake Error");

    const post = await tx.post.findUniqueOrThrow({
      where: {
        id: postId,
        isPremium: false,
      },
      include: {
        author: {
          omit: {
            password: true,
          },
        },
        comments: {
          where: {
            status: CommentStatus.APPROVED,
          },
          orderBy: {
            createdAt: "desc",
          },
        },

        _count: {
          select: {
            comments: true,
          },
        },
      },
    });
    return post;
  });
  return transactionResult;
};

const getPostsStats = async () => {
  const transactionResult = await prisma.$transaction(async (tx) => {
    const [
      totalPost,
      totalPublishedPost,
      totalDraftPost,
      totalArchivedPost,
      totalComments,
      totalApprovedComments,
      totalRejectedComments,
      totalPostViewsAggregate,
    ] = await Promise.all([
      await tx.post.count(),
      await tx.post.count({
        where: {
          status: PostStatus.PUBLISHED,
        },
      }),
      await tx.post.count({
        where: {
          status: PostStatus.DRAFT,
        },
      }),
      await tx.post.count({
        where: {
          status: PostStatus.ARCHIVED,
        },
      }),
      await tx.comment.count(),
      await tx.comment.count({
        where: {
          status: CommentStatus.APPROVED,
        },
      }),
      await tx.comment.count({
        where: {
          status: CommentStatus.REJECT,
        },
      }),
      await tx.post.aggregate({
        _sum: {
          views: true,
        },
      }),
    ]);
    return {
      totalPost,
      totalPublishedPost,
      totalDraftPost,
      totalArchivedPost,
      totalComments,
      totalApprovedComments,
      totalRejectedComments,
      totalPostViews: totalPostViewsAggregate._sum.views,
    };
  });
  return transactionResult;
};

const getMyPosts = async (authorId: string) => {
  const result = await prisma.post.findMany({
    where: {
      authorId,
    },
    orderBy: {
      createdAt: "desc",
    },
    include: {
      author: {
        omit: {
          password: true,
        },
      },
      _count: {
        select: {
          comments: true,
        },
      },
      comments: true,
    },
  });

  return result;
};

const updatePost = async (
  postId: string,
  payload: IUpdatePostPayload,
  authorId: string,
  isAdmin: boolean,
) => {
  const post = await prisma.post.findUniqueOrThrow({
    where: {
      id: postId,
    },
  });

  if (!isAdmin && post.authorId !== authorId) {
    throw new Error("Access denied! you are not owner of this post or admin.");
  }

  const result = await prisma.post.update({
    where: {
      id: postId,
    },
    data: payload,
    include: {
      author: {
        omit: {
          password: true,
        },
      },
      comments: true,
    },
  });

  return result;
};

const deletePost = async (
  postId: string,
  authorId: string,
  isAdmin: boolean,
) => {
  const post = await prisma.post.findUniqueOrThrow({
    where: {
      id: postId,
    },
  });

  if (!isAdmin && post.authorId !== authorId) {
    throw new Error("Access denied! you are not owner of this post or admin.");
  }

  await prisma.post.delete({
    where: {
      id: postId,
    },
  });
};

export const postService = {
  createPost,
  getAllPosts,
  getAllPostsWithFilter,
  getAllPostsWithSearch,
  getAllPostsWithFilterAndSearch,
  getAllPostsWithPagination,
  getAllPostsWithDynamicPagination,
  getPostById,
  getPostsStats,
  getMyPosts,
  updatePost,
  deletePost,
};
